import React, { useEffect, useState } from "react";
import { getProducts } from "@/lib/api";
import { ExternalLink, Sparkles, Tag, Star, ShoppingCart } from "lucide-react";

/**
 * AffiliateStrip — surfaces affiliate products managed via Admin → Affiliate Links.
 * Placed on Home, Resources, Blog posts, Writing posts, Tools Hub, Tool Detail, Recommended page.
 *
 * variant: "grid" | "row" | "sidebar" | "banner"
 *
 * When a product's affiliate_url points to Amazon (amazon.com / amazon.fr /
 * amazon.co.uk / amzn.to / amzn.eu / etc.), the card renders in "native Amazon"
 * mode: light background, star-rating slot, big yellow-orange "Buy on Amazon"
 * button. Everything else stays generic.
 */

const AMAZON_RE = /(amazon\.(com|fr|co\.uk|de|es|it|ca|com\.mx|co\.jp|com\.au|nl|se|pl|sg|ae|sa|com\.br|com\.tr|eg)|amzn\.to|amzn\.eu|a\.co)/i;
const isAmazonUrl = (u) => !!u && AMAZON_RE.test(u);

function StarRow({ rating }) {
  if (!rating) return null;
  const r = Math.max(0, Math.min(5, Number(rating)));
  const full = Math.floor(r);
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={`w-3 h-3 ${i < full ? "fill-amber-400 text-amber-400" : "text-slate-300"}`}
        />
      ))}
      <span className="ml-1 text-[10px] font-mono text-slate-500">{r.toFixed(1)}</span>
    </div>
  );
}

/**
 * AmazonCard — replaces the generic affiliate card whenever the product's URL
 * is an Amazon link. Same footprint, same click surface — just Amazon-styled.
 */
function AmazonCard({ product }) {
  const url = product.affiliate_url || "#";
  const price = product.price ? (product.currency === "EUR" ? `${product.price} €` : `$${product.price}`) : null;
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer sponsored"
      data-testid={`amazon-item-${product.id}`}
      className="group flex flex-col h-full border border-[#febd69]/50 bg-white rounded-xl p-4 hover:border-[#febd69] hover:shadow-lg transition-all"
    >
      {product.image && (
        <div className="aspect-video w-full overflow-hidden rounded-lg bg-slate-50 mb-3 flex items-center justify-center">
          <img
            src={product.image}
            alt={product.name}
            className="w-full h-full object-contain group-hover:scale-[1.03] transition-transform duration-500"
          />
        </div>
      )}
      <p className="text-[10px] font-mono uppercase tracking-widest text-[#232F3E] mb-1 flex items-center gap-1">
        <ShoppingCart className="w-3 h-3" /> Amazon · Affiliate
      </p>
      <p className="font-medium text-sm leading-snug line-clamp-2 group-hover:text-[#232F3E]">
        {product.name}
      </p>
      {product.rating && <div className="mt-1"><StarRow rating={product.rating} /></div>}
      {product.description && (
        <p className="text-xs text-slate-500 mt-1 line-clamp-2 flex-1">{product.description}</p>
      )}
      <div className="mt-3 flex items-center justify-between">
        {price ? (
          <span className="font-display text-base text-[#B12704] font-semibold">{price}</span>
        ) : (
          <span className="font-mono text-[11px] text-slate-500">See price on Amazon</span>
        )}
        <span className="inline-flex items-center gap-1.5 bg-[#febd69] hover:bg-[#f3a847] text-[#111] text-xs font-semibold px-3 py-1.5 rounded-full transition-colors">
          Buy on Amazon
          <ExternalLink className="w-3 h-3" />
        </span>
      </div>
    </a>
  );
}

export default function AffiliateStrip({
  title = "Tools & products I recommend",
  subtitle = "Hand-picked resources — some of these are affiliate links, meaning I may earn a small commission at no extra cost to you.",
  limit = 6,
  variant = "grid",
  category = null,
  tags = [],
  className = "",
}) {
  const [items, setItems] = useState([]);

  useEffect(() => {
    getProducts({ type: "affiliate" })
      .then((all) => {
        // Contextual match: try tags first, then category, then fall back to the full list.
        // Score each product by how many of the article's tags overlap with the product's
        // tags/category/name so blog posts about "SEO" surface SEO tools, not random gear.
        const normalize = (s) => (s || "").toString().toLowerCase();
        const wantTags = (tags || []).map(normalize).filter(Boolean);
        const wantCat = normalize(category);

        let filtered = all;
        if (wantTags.length || wantCat) {
          const scored = all.map((p) => {
            const bag = [
              ...(p.tags || []),
              p.category,
              p.name,
              p.description,
            ].map(normalize).join(" | ");
            let score = 0;
            wantTags.forEach((t) => { if (t && bag.includes(t)) score += 2; });
            if (wantCat && bag.includes(wantCat)) score += 3;
            return { p, score };
          });
          const contextual = scored.filter((x) => x.score > 0)
            .sort((a, b) => b.score - a.score)
            .map((x) => x.p);
          filtered = contextual.length ? contextual : all;
        }
        setItems(filtered.slice(0, limit));
      })
      .catch(() => setItems([]));
  }, [limit, category, JSON.stringify(tags)]);

  if (!items.length) return null;

  if (variant === "banner") {
    const it = items[0];
    const amz = isAmazonUrl(it.affiliate_url);
    return (
      <a
        href={it.affiliate_url || "#"}
        target="_blank"
        rel="noreferrer sponsored"
        data-testid="affiliate-banner"
        className={`group flex items-center gap-4 p-5 rounded-2xl border ${amz ? "border-[#febd69]/60 bg-gradient-to-r from-[#fff8e7] to-white" : "border-indigo-100 bg-gradient-to-r from-indigo-50 to-sky-50 hover:from-indigo-100 hover:to-sky-100"} transition ${className}`}
      >
        {it.image && <img src={it.image} alt="" className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />}
        <div className="flex-1 min-w-0">
          <p className={`text-[10px] font-mono uppercase tracking-widest flex items-center gap-1 ${amz ? "text-[#232F3E]" : "text-indigo-600"}`}>
            {amz ? <ShoppingCart className="w-3 h-3" /> : <Sparkles className="w-3 h-3" />}
            {amz ? "Amazon · Affiliate" : "Recommended · Affiliate"}
          </p>
          <p className="font-medium truncate">{it.name}</p>
          {it.description && <p className="text-xs text-slate-500 line-clamp-1">{it.description}</p>}
        </div>
        <ExternalLink className={`w-4 h-4 group-hover:translate-x-0.5 transition-transform flex-shrink-0 ${amz ? "text-[#B12704]" : "text-indigo-600"}`} />
      </a>
    );
  }

  if (variant === "sidebar") {
    return (
      <aside data-testid="affiliate-sidebar" className={`space-y-3 ${className}`}>
        <p className="overline text-indigo-600 flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> Recommended</p>
        {items.map((p) => {
          const amz = isAmazonUrl(p.affiliate_url);
          return (
            <a key={p.id} href={p.affiliate_url || "#"} target="_blank" rel="noreferrer sponsored"
               data-testid={`affiliate-item-${p.id}`}
               className={`group block border rounded-xl bg-white p-3 hover-lift ${amz ? "border-[#febd69]/50 hover:border-[#febd69]" : "border-slate-100"}`}>
              <div className="flex items-center gap-3">
                {p.image && <img src={p.image} alt="" className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />}
                <div className="flex-1 min-w-0">
                  <p className={`font-medium text-sm truncate ${amz ? "group-hover:text-[#232F3E]" : "group-hover:text-indigo-600"}`}>{p.name}</p>
                  <p className="text-[11px] text-slate-500 truncate">{amz ? "Amazon" : p.category}</p>
                </div>
                <ExternalLink className={`w-3.5 h-3.5 ${amz ? "text-[#B12704]" : "text-slate-400 group-hover:text-indigo-600"}`} />
              </div>
            </a>
          );
        })}
        <p className="text-[10px] text-slate-400 leading-relaxed pt-1">Affiliate disclosure: I may earn a commission from qualifying purchases.</p>
      </aside>
    );
  }

  if (variant === "row") {
    return (
      <div data-testid="affiliate-row" className={`flex gap-4 overflow-x-auto pb-2 ${className}`}>
        {items.map((p) =>
          isAmazonUrl(p.affiliate_url) ? (
            <div key={p.id} className="flex-shrink-0 w-64"><AmazonCard product={p} /></div>
          ) : (
            <a key={p.id} href={p.affiliate_url || "#"} target="_blank" rel="noreferrer sponsored" data-testid={`affiliate-item-${p.id}`} className="group flex-shrink-0 w-64 border border-slate-100 rounded-xl bg-white p-4 hover-lift">
              {p.image && <img src={p.image} alt="" className="w-full aspect-video object-cover rounded-lg mb-3" />}
              <p className="text-[10px] font-mono uppercase tracking-widest text-indigo-600 mb-1">{p.category}</p>
              <p className="font-medium text-sm group-hover:text-indigo-600 line-clamp-1">{p.name}</p>
              <span className="inline-flex items-center gap-1 text-xs text-indigo-600 mt-2">Get it <ExternalLink className="w-3 h-3" /></span>
            </a>
          )
        )}
      </div>
    );
  }

  // grid (default)
  return (
    <section data-testid="affiliate-grid" className={className}>
      <div className="flex items-end justify-between mb-6 gap-4">
        <div>
          <p className="overline text-indigo-600 flex items-center gap-1.5 mb-2"><Sparkles className="w-3.5 h-3.5" /> Affiliate picks</p>
          <h3 className="font-display text-2xl md:text-3xl">{title}</h3>
          {subtitle && <p className="text-sm text-slate-500 mt-1 max-w-2xl">{subtitle}</p>}
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {items.map((p) =>
          isAmazonUrl(p.affiliate_url) ? (
            <AmazonCard key={p.id} product={p} />
          ) : (
            <a key={p.id} href={p.affiliate_url || "#"} target="_blank" rel="noreferrer sponsored" data-testid={`affiliate-item-${p.id}`} className="group border border-slate-100 rounded-xl bg-white p-4 hover-lift flex flex-col">
              {p.image && <img src={p.image} alt={p.name} className="w-full aspect-video object-cover rounded-lg mb-3" />}
              <p className="text-[10px] font-mono uppercase tracking-widest text-indigo-600 mb-1 flex items-center gap-1"><Tag className="w-3 h-3" /> {p.category}</p>
              <p className="font-medium group-hover:text-indigo-600 line-clamp-1">{p.name}</p>
              {p.description && <p className="text-xs text-slate-500 mt-1 line-clamp-2 flex-1">{p.description}</p>}
              <span className="inline-flex items-center gap-1 text-xs text-indigo-600 mt-3">View deal <ExternalLink className="w-3 h-3" /></span>
            </a>
          )
        )}
      </div>
    </section>
  );
}
